import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { IconBed, IconBedOff, IconUserPlus, IconCash, IconPlus, IconBrush, IconCheck, IconTrash, IconFileText, IconCalendar, IconInfoCircle, IconDiscount, IconScale, IconPercentage, IconX, IconClock } from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";
import { useTranslation } from "react-i18next";
import { usePaymentTypes, usePrintSettings, useStoreSettings } from "../controllers/settings.controller";
import { generateRoomBillHTML, BILL_FORMATS } from "../helpers/BillFormatsHelper";
import { printReceipt } from "../helpers/PrinterHelper";
import { setDetailsForReceiptPrint } from "../helpers/ReceiptHelper";
import OfflineStorage from "../helpers/OfflineStorage";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);

const safeNum = (v) => {
  const n = Number(String(v ?? 0).replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};

const getElapsedHoursText = (dateStr) => {
  if (!dateStr) return null;
  const m = moment(dateStr);
  if (!m.isValid()) return null;
  const now = moment();
  const totalMinutes = Math.max(0, now.diff(m, 'minutes'));

  if (totalMinutes < 1) return '< 1m';
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("PMS Modal Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center space-y-4">
          <p className="text-red-500 font-bold text-base">Failed to display details for this room.</p>
          <p className="text-xs text-gray-500 font-mono max-w-md mx-auto break-words">{this.state.error?.message || "Unknown error"}</p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 bg-restro-green text-white text-xs font-bold rounded-xl hover:bg-restro-green-button-hover"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function RoomsStatusPage() {
  const { t } = useTranslation();
  const { data: printSettings } = usePrintSettings();
  const { data: storeSettings } = useStoreSettings();
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(timer);
  }, []);
  
  // Selection/Modals state
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("folio"); // folio, charge, checkin, checkout
  
  // Form states
  const [checkInForm, setCheckInForm] = useState({
    guest_name: "",
    guest_phone: "",
    guest_email: "",
    room_type_id: "",
    room_number: "",
    check_in: new Date().toISOString().slice(0, 16),
    check_out: "",
    advance_payment: 0,
    payment_method: "Cash",
    notes: ""
  });
  const [extraChargeForm, setExtraChargeForm] = useState({ title: "", amount: "" });
  const [checkoutForm, setCheckoutForm] = useState({
    paymentType: "Cash",
    multiPayments: [],
    discount: 0,
    discountType: "fixed",
    roundOff: 0,
    targetTotal: undefined,
    serviceCharge: 0,
    paidAmount: "",
    note: ""
  });
  const [paymentForm, setPaymentForm] = useState({ amount: "", paymentMethod: "Cash" });
  const [taxes, setTaxes] = useState([]);
  const { data: paymentTypes } = usePaymentTypes();

  // Split payment helpers for Checkout
  const addCheckoutMultiPaymentRow = () => {
    const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
    const discountVal = safeNum(checkoutForm.discount);
    const discountAmount = checkoutForm.discountType === "percent" ? (subTotal * discountVal) / 100 : discountVal;
    const baseAmount = Math.max(0, subTotal - discountAmount) + safeNum(checkoutForm.serviceCharge);
    let totalExclusiveTax = 0;
    (Array.isArray(taxes) ? taxes : []).forEach(tax => {
      const rate = safeNum(tax.rate);
      if (tax.type === "exclusive") totalExclusiveTax += (baseAmount * rate) / 100;
    });
    const netPayable = Math.max(0, baseAmount + totalExclusiveTax - safeNum(folioData?.payments) + safeNum(checkoutForm.roundOff));
    const totalToPay = checkoutForm.paidAmount !== "" && checkoutForm.paidAmount !== undefined
      ? safeNum(checkoutForm.paidAmount)
      : netPayable;
    
    const currentPaid = (checkoutForm.multiPayments || []).reduce((acc, p) => acc + (parseFloat(String(p.amount || 0).replace(/,/g, "")) || 0), 0);
    const remaining = Math.max(0, Math.round((totalToPay - currentPaid) * 100) / 100);

    const activePTs = Array.isArray(paymentTypes) ? paymentTypes.filter(p => p.is_active) : [];
    const firstType = activePTs[0] || { id: "Cash", title: "Cash" };

    setCheckoutForm(prev => ({
      ...prev,
      multiPayments: [
        ...(prev.multiPayments || []),
        { payment_type_id: firstType.id, title: firstType.title, amount: remaining }
      ]
    }));
  };

  const removeCheckoutMultiPaymentRow = (index) => {
    setCheckoutForm(prev => {
      const updated = [...(prev.multiPayments || [])];
      updated.splice(index, 1);
      return { ...prev, multiPayments: updated };
    });
  };

  const handleCheckoutMultiPaymentChange = (index, field, value) => {
    setCheckoutForm(prev => {
      const updated = [...(prev.multiPayments || [])];
      if (field === 'payment_type_id') {
        const activePTs = Array.isArray(paymentTypes) ? paymentTypes : [];
        const type = activePTs.find(t => String(t.id) === String(value));
        updated[index].payment_type_id = value;
        updated[index].title = type?.title || value;
      } else {
        updated[index][field] = value;
      }
      return { ...prev, multiPayments: updated };
    });
  };

  // Folio state
  const [folioData, setFolioData] = useState(null);
  const [folioLoading, setFolioLoading] = useState(false);
  const [isEditingStay, setIsEditingStay] = useState(false);
  const [editStayForm, setEditStayForm] = useState({ nights: "", roomRate: "" });

  const [targetRoomName, setTargetRoomName] = useState("");
  const [targetBookingId, setTargetBookingId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (Array.isArray(paymentTypes) && paymentTypes.length > 0) {
      const activeMethods = paymentTypes.filter(p => p.is_active);
      if (activeMethods.length > 0) {
        const defaultMethodTitle = activeMethods[0].title;
        const defaultMethodId = activeMethods[0].id;
        setPaymentForm(prev => ({
          ...prev,
          paymentMethod: prev.paymentMethod === "Cash" && !activeMethods.some(m => m.title === "Cash") ? defaultMethodTitle : prev.paymentMethod
        }));
        setCheckInForm(prev => ({
          ...prev,
          payment_method: prev.payment_method === "Cash" && !activeMethods.some(m => m.title === "Cash") ? defaultMethodTitle : prev.payment_method
        }));
        setCheckoutForm(prev => ({
          ...prev,
          paymentType: (!prev.paymentType || prev.paymentType === "Cash" || !activeMethods.some(m => String(m.id) === String(prev.paymentType))) ? defaultMethodId : prev.paymentType
        }));
      }
    }
  }, [paymentTypes]);

  const fetchData = async () => {
    setLoading(true);
    const isOnline = OfflineStorage.isClientOnline();
    if (!isOnline) {
      const cachedRooms = OfflineStorage.getCachedRooms();
      const cachedTypes = OfflineStorage.getCachedRoomTypes();
      if (Array.isArray(cachedRooms) && cachedRooms.length > 0) {
        setRooms(cachedRooms);
        setRoomTypes(cachedTypes || []);
        setLoading(false);
        return;
      }
    }

    try {
      const [roomsRes, typesRes, custRes, taxesRes] = await Promise.all([
        apiClient.get("/rooms"),
        apiClient.get("/rooms/types"),
        apiClient.get("/customers/download/all").catch(() => ({ data: [] })),
        apiClient.get("/settings/taxes").catch(() => ({ data: [] }))
      ]);
      const rawRooms = roomsRes.data || [];
      const seenNames = new Set();
      const validRooms = rawRooms.filter(r => {
        if (!r || !r.name) return false;
        const cleanName = String(r.name).trim();
        if (!cleanName || cleanName === 'null' || cleanName.includes('16000001')) return false;
        const key = cleanName.toLowerCase();
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });
      setRooms(validRooms);
      setRoomTypes(typesRes.data || []);
      setCustomersList(custRes.data || []);
      setTaxes(taxesRes.data || []);

      // Cache locally for offline usage
      OfflineStorage.cacheRooms(validRooms, typesRes.data);
    } catch (e) {
      console.warn("RoomsStatusPage network fetch failed, using offline cache:", e);
      const cachedRooms = OfflineStorage.getCachedRooms();
      const cachedTypes = OfflineStorage.getCachedRoomTypes();
      if (Array.isArray(cachedRooms) && cachedRooms.length > 0) {
        setRooms(cachedRooms);
        setRoomTypes(cachedTypes || []);
      } else {
        toast.error("Failed to load PMS data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoomClick = async (room, initialTab = null) => {
    setFolioData(null);
    setSelectedRoom(room);
    setIsModalOpen(true);
    setIsEditingStay(false);
    setCheckoutForm(prev => ({
      ...prev,
      discount: 0,
      discountType: "fixed",
      roundOff: 0,
      targetTotal: undefined,
      serviceCharge: 0,
      paidAmount: "",
      note: ""
    }));
    if (room?.active_booking_id) {
      setModalTab(initialTab || "folio");
      fetchFolio(room.active_booking_id);
    } else if (room?.arrival_booking_id) {
      setModalTab(initialTab || "folio");
      fetchFolio(room.arrival_booking_id);
      setCheckInForm({
        ...checkInForm,
        room_type_id: room?.room_type_id || "",
        room_number: room?.name || "",
        check_in: new Date().toISOString().slice(0, 16),
        guest_name: room?.arrival_guest || "",
        guest_phone: room?.arrival_guest_phone || "",
        guest_email: room?.arrival_guest_email || ""
      });
    } else {
      setModalTab(initialTab || "checkin");
      setCheckInForm({
        ...checkInForm,
        room_type_id: room?.room_type_id || "",
        room_number: room?.name || "",
        check_in: new Date().toISOString().slice(0, 16),
        guest_name: room?.arrival_guest || "",
        guest_phone: room?.arrival_guest_phone || "",
        guest_email: room?.arrival_guest_email || ""
      });
    }
  };

  const handleCustomerSelect = (e) => {
    const custId = e.target.value;
    if(!custId) {
      setCheckInForm({...checkInForm, guest_name: "", guest_phone: "", guest_email: ""});
      return;
    }
    const cust = customersList.find(c => c.phone === custId);
    if(cust) {
      setCheckInForm({...checkInForm, guest_name: cust.name, guest_phone: cust.phone, guest_email: cust.email || ""});
    }
  };

  const fetchFolio = async (bookingId) => {
    if (!bookingId) {
      setFolioData(null);
      return;
    }
    setFolioLoading(true);
    try {
      const res = await apiClient.get(`/rooms/bookings/${bookingId}/folio`);
      setFolioData(res.data || null);
    } catch (e) {
      toast.error("Failed to fetch guest folio");
      setFolioData(null);
    } finally {
      setFolioLoading(false);
    }
  };

  const handleSaveStayCustomization = async (e) => {
    if (e) e.preventDefault();
    const bookingId = selectedRoom?.active_booking_id || selectedRoom?.arrival_booking_id;
    if (!bookingId) return;
    try {
      await apiClient.post(`/rooms/bookings/${bookingId}/update-stay`, {
        nights: Number(String(editStayForm.nights).replace(/,/g, "")),
        room_rate: Number(String(editStayForm.roomRate).replace(/,/g, ""))
      });
      toast.success("Stay details updated successfully!");
      setIsEditingStay(false);
      fetchFolio(bookingId);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update stay details.");
    }
  };

  const handlePrintFolio = (formatOverride, isEstimate = false, checkoutOverride = null) => {
    if (!folioData) return;
    const currency = storeSettings?.currency || "Rs.";
    const activeFormat = formatOverride || printSettings?.billFormat || printSettings?.bill_format || localStorage.getItem('bill_format') || 'pos_receipt';
    const subTotal = safeNum(folioData.roomTotal) + safeNum(folioData.extraTotal) + safeNum(folioData.orderTotal);
    
    const discountAmount = checkoutOverride?.discount !== undefined
      ? safeNum(checkoutOverride.discount)
      : (checkoutForm.discountType === "percent"
          ? (subTotal * safeNum(checkoutForm.discount)) / 100
          : safeNum(checkoutForm.discount));

    const roundOff = checkoutOverride?.roundOff !== undefined
      ? safeNum(checkoutOverride.roundOff)
      : safeNum(checkoutForm.roundOff);

    const serviceCharge = checkoutOverride?.serviceCharge !== undefined
      ? safeNum(checkoutOverride.serviceCharge)
      : safeNum(checkoutForm.serviceCharge);

    const advancePaid = safeNum(folioData.payments);
    const afterDiscount = Math.max(0, subTotal - discountAmount);
    const baseAmount = afterDiscount + serviceCharge;
    let totalExclusiveTax = 0;
    (Array.isArray(taxes) ? taxes : []).forEach(tax => {
      const rate = safeNum(tax.rate);
      if (tax.type === "exclusive") totalExclusiveTax += (baseAmount * rate) / 100;
    });
    const totalAmount = checkoutOverride?.totalAmount !== undefined
      ? safeNum(checkoutOverride.totalAmount)
      : Math.round((baseAmount + totalExclusiveTax + roundOff) * 100) / 100;

    let paidAmount;
    let dueAmount;
    let amountPaidNow = 0;

    if (checkoutOverride) {
      paidAmount = safeNum(checkoutOverride.paidAmount);
      dueAmount = safeNum(checkoutOverride.dueAmount);
      amountPaidNow = safeNum(checkoutOverride.amountPaidNow !== undefined ? checkoutOverride.amountPaidNow : Math.max(0, paidAmount - advancePaid));
    } else if (isEstimate) {
      paidAmount = advancePaid;
      dueAmount = Math.max(0, totalAmount - paidAmount);
      amountPaidNow = 0;
    } else {
      // Direct print without checkout response
      const enteredPaid = checkoutForm.paidAmount === "" ? null : Number(String(checkoutForm.paidAmount).replace(/,/g, ""));
      if (enteredPaid === null) {
        paidAmount = totalAmount;
        dueAmount = 0;
        amountPaidNow = Math.max(0, totalAmount - advancePaid);
      } else {
        amountPaidNow = Math.max(0, enteredPaid);
        paidAmount = Math.min(totalAmount, advancePaid + amountPaidNow);
        dueAmount = Math.max(0, Math.round((totalAmount - paidAmount) * 100) / 100);
      }
    }

    const finalInvoiceNo = checkoutOverride?.invoiceNo || folioData.booking?.invoice_no;
    const checkoutNote = checkoutOverride?.note || checkoutForm.note || folioData.booking?.notes || "";
    const taxAmountToUse = checkoutOverride?.taxAmount !== undefined
      ? safeNum(checkoutOverride.taxAmount)
      : Math.round(totalExclusiveTax * 100) / 100;

    const enrichedFolioData = {
      ...folioData,
      note: checkoutNote,
      notes: checkoutNote,
      invoiceNo: finalInvoiceNo,
      invoiceId: checkoutOverride?.invoiceId || finalInvoiceNo,
      billNo: finalInvoiceNo,
      booking: {
        ...folioData.booking,
        notes: checkoutNote || folioData.booking?.notes,
        invoice_no: finalInvoiceNo || folioData.booking?.invoice_no
      },
      subTotal: subTotal,
      itemsTotal: subTotal,
      discount: discountAmount,
      storedDiscount: discountAmount,
      roundOff: roundOff,
      serviceCharge: serviceCharge,
      serviceChargeTotal: serviceCharge,
      taxAmount: taxAmountToUse,
      taxTotal: taxAmountToUse,
      taxableAmount: baseAmount,
      taxes: taxes,
      totalAmount: totalAmount,
      payableTotal: totalAmount,
      paidAmount: paidAmount,
      dueAmount: dueAmount,
      advancePayment: advancePaid,
      amountPaidNow: amountPaidNow,
      paymentType: checkoutOverride?.paymentType || checkoutForm.paymentType || "Cash",
      paymentMethod: (Array.isArray(paymentTypes) && paymentTypes.find(p => p.id == (checkoutOverride?.paymentType || checkoutForm.paymentType || "Cash")))?.title || checkoutOverride?.paymentType || checkoutForm.paymentType || "Cash",
      multiPayments: checkoutOverride?.multiPayments || checkoutForm.multiPayments || []
    };

    // Construct cartItems for thermal / POS printer
    const nights = safeNum(folioData.nights || 1);
    const roomRate = safeNum(folioData.booking?.room_rate);
    const cartItems = [
      {
        title: `Room Stay: Room ${folioData.booking?.room_number || ''} (${folioData.booking?.room_type_name || 'Standard'})`,
        subtitle: `${nights} Night(s) @ ${currency} ${roomRate.toFixed(2)}`,
        quantity: nights,
        qty: nights,
        price: roomRate,
        rate: roomRate,
        total: roomRate * nights
      }
    ];

    if (Array.isArray(folioData.extraCharges)) {
      folioData.extraCharges.forEach(ec => {
        const amt = safeNum(ec.amount);
        cartItems.push({
          title: `Service Charge: ${ec.title || 'Extra Service'}`,
          subtitle: 'Amenity / Service',
          quantity: 1,
          qty: 1,
          price: amt,
          rate: amt,
          total: amt
        });
      });
    }

    if (Array.isArray(folioData.orders)) {
      folioData.orders.forEach(ord => {
        if (Array.isArray(ord.items) && ord.items.length > 0) {
          ord.items.forEach(i => {
            if (i.status === 'cancelled') return;
            const itemPrice = safeNum(i.price || i.catalog_price || i.rate);
            const itemQty = safeNum(i.quantity || 1);
            cartItems.push({
              title: i.item_name || i.title || 'Restaurant Item',
              variant_title: i.variant_title || '',
              quantity: itemQty,
              qty: itemQty,
              price: itemPrice,
              rate: itemPrice,
              total: itemPrice * itemQty
            });
          });
        } else {
          const ordTot = safeNum(ord.order_total);
          if (ordTot > 0) {
            cartItems.push({
              title: `Restaurant: Order #${ord.id}`,
              quantity: 1,
              qty: 1,
              price: ordTot,
              rate: ordTot,
              total: ordTot
            });
          }
        }
      });
    }

    const receiptDetails = {
      cartItems: cartItems,
      deliveryType: 'room',
      table_title: `Room ${folioData.booking?.room_number || ''}`,
      room_name: String(folioData.booking?.room_number || ''),
      source: String(folioData.booking?.room_number || ''),
      currency: currency,
      storeSettings: storeSettings,
      printSettings: printSettings,
      itemsTotal: subTotal,
      taxTotal: taxAmountToUse,
      taxableAmount: baseAmount,
      serviceChargeTotal: serviceCharge,
      storedDiscount: discountAmount,
      roundOff: roundOff,
      payableTotal: totalAmount,
      paidAmount: paidAmount,
      dueAmount: dueAmount,
      tokenNo: folioData.booking?.id || '',
      orderId: `RM-${folioData.booking?.id || ''}`,
      invoiceId: finalInvoiceNo,
      invoice_id: finalInvoiceNo,
      billNo: finalInvoiceNo,
      invoiceNo: finalInvoiceNo,
      paymentMethod: (Array.isArray(paymentTypes) && paymentTypes.find(p => p.id == (checkoutOverride?.paymentType || checkoutForm.paymentType || "Cash")))?.title || checkoutOverride?.paymentType || checkoutForm.paymentType || "Cash",
      multiPayments: checkoutOverride?.multiPayments || checkoutForm.multiPayments || [],
      customer: {
        name: folioData.booking?.guest_name || 'Guest',
        phone: folioData.booking?.guest_phone || '',
        pan_no: folioData.booking?.guest_pan || folioData.booking?.pan_no || ''
      },
      note: checkoutNote,
      notes: checkoutNote,
      isEstimate: isEstimate,
      date: new Date()
    };

    setDetailsForReceiptPrint(receiptDetails);

    if (activeFormat === "pos_receipt") {
      printReceipt({
        ...receiptDetails,
        printSettings,
        pageFormat: printSettings?.page_format || localStorage.getItem('page_format') || '58'
      });
      return;
    }

    try {
      const printWindow = window.open("", "_blank", "width=850,height=750");
      if (!printWindow) {
        toast("Bill generated! (Please allow popups to auto-print bill)", { icon: "ℹ️" });
        return;
      }
      const htmlContent = generateRoomBillHTML(enrichedFolioData, activeFormat, printSettings, storeSettings, isEstimate);
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (printErr) {
      console.warn("Print window error:", printErr);
    }
  };

  const handleChangeRoom = async (e) => {
    e.preventDefault();
    if (!targetRoomName) {
      toast.error("Please select a target room");
      return;
    }
    try {
      toast.loading("Changing room...");
      const res = await apiClient.post(`/rooms/bookings/${selectedRoom.active_booking_id}/change-room`, {
        targetRoomName
      });
      toast.dismiss();
      toast.success(res.data.message || "Room changed successfully!");
      setIsModalOpen(false);
      setTargetRoomName("");
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to change room");
    }
  };

  const handleMergeRooms = async (e) => {
    e.preventDefault();
    if (!targetBookingId) {
      toast.error("Please select a room to merge into");
      return;
    }
    try {
      toast.loading("Merging room bills...");
      const res = await apiClient.post(`/rooms/bookings/${selectedRoom.active_booking_id}/merge`, {
        targetBookingId
      });
      toast.dismiss();
      toast.success(res.data.message || "Rooms merged successfully!");
      setIsModalOpen(false);
      setTargetBookingId("");
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to merge rooms");
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    try {
      const isReserve = modalTab === "reserve";
      const roomTypeId = checkInForm.room_type_id || selectedRoom?.room_type_id;
      const selectedType = roomTypes.find(t => String(t.id) === String(roomTypeId));
      const roomRate = selectedType ? Number(selectedType.rate || 0) : 0;
      const advance = Number(String(checkInForm.advance_payment || 0).replace(/,/g, ""));

      // 1. If checking in an already reserved room arrival
      if (selectedRoom?.arrival_booking_id && !isReserve) {
        await apiClient.post(`/rooms/bookings/${selectedRoom.arrival_booking_id}/check-in`, {
          check_in: checkInForm.check_in,
          advance_payment: advance,
          payment_method: checkInForm.payment_method
        });
        toast.success("Guest checked in successfully!");
        setIsModalOpen(false);
        fetchData();
        return;
      }

      // 2. Direct creation (Instant Check-in or Reserve)
      const bookingPayload = {
        guest_name: checkInForm.guest_name,
        guest_phone: checkInForm.guest_phone,
        guest_email: checkInForm.guest_email,
        room_type_id: roomTypeId,
        room_number: checkInForm.room_number || selectedRoom?.name,
        check_in: checkInForm.check_in,
        check_out: checkInForm.check_out || null,
        notes: checkInForm.notes,
        room_rate: roomRate,
        amount: roomRate,
        advance_payment: advance,
        payment_method: checkInForm.payment_method,
        status: isReserve ? "Pending" : "Checked-in"
      };

      await apiClient.post("/rooms/bookings", bookingPayload);

      if (isReserve) {
        toast.success("Room reserved successfully!");
      } else {
        toast.success("Guest checked in successfully!");
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const bookingId = selectedRoom?.active_booking_id || selectedRoom?.arrival_booking_id;
    if (!bookingId) return;
    try {
      toast.loading("Recording payment...");
      const res = await apiClient.post(`/rooms/bookings/${bookingId}/payments`, {
        amount: Number(String(paymentForm.amount).replace(/,/g, "")),
        paymentMethod: paymentForm.paymentMethod
      });
      toast.dismiss();
      toast.success(res.data.message || "Payment recorded successfully!");
      setPaymentForm({ amount: "", paymentMethod: "Cash" });
      fetchFolio(bookingId);
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to record payment");
    }
  };

  const handleAddCharge = async (e) => {
    e.preventDefault();
    if (!extraChargeForm.title || !extraChargeForm.amount) return;
    try {
      await apiClient.post(`/rooms/bookings/${selectedRoom.active_booking_id}/extra-charges`, {
        title: extraChargeForm.title,
        amount: Number(String(extraChargeForm.amount).replace(/,/g, ""))
      });
      toast.success("Service charge added to folio");
      setExtraChargeForm({ title: "", amount: "" });
      fetchFolio(selectedRoom.active_booking_id);
    } catch (err) {
      toast.error("Failed to add charge");
    }
  };

  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleCheckOut = async (e) => {
    if (e) e.preventDefault();
    if (!folioData) {
      toast.error("Folio data is not loaded yet. Please wait.");
      return;
    }
    if (isCheckingOut) return;

    try {
      setIsCheckingOut(true);
      // Folio values
      const subTotal = safeNum(folioData.roomTotal) + safeNum(folioData.extraTotal) + safeNum(folioData.orderTotal);
      const discountVal = safeNum(checkoutForm.discount);
      const discount = checkoutForm.discountType === "percent"
        ? (subTotal * discountVal) / 100
        : discountVal;
      const afterDiscount = Math.max(0, subTotal - discount);
      const serviceCharge = safeNum(checkoutForm.serviceCharge);
      const baseAmount = afterDiscount + serviceCharge;

      // Calculate taxes dynamically based on settings
      let totalExclusiveTax = 0;
      let totalInclusiveTax = 0;
      (Array.isArray(taxes) ? taxes : []).forEach(tax => {
        const rate = safeNum(tax.rate);
        if (tax.type === "exclusive") {
          totalExclusiveTax += (baseAmount * rate) / 100;
        } else if (tax.type === "inclusive") {
          totalInclusiveTax += baseAmount - (baseAmount * (100 / (100 + rate)));
        }
      });

      const taxAmount = Math.round((totalExclusiveTax + totalInclusiveTax) * 100) / 100;
      const roundOff = safeNum(checkoutForm.roundOff);
      const totalAmount = Math.round((baseAmount + totalExclusiveTax + roundOff) * 100) / 100;

      if (Array.isArray(checkoutForm.multiPayments) && checkoutForm.multiPayments.length > 0) {
        const sum = checkoutForm.multiPayments.reduce((acc, p) => acc + (parseFloat(String(p.amount || 0).replace(/,/g, "")) || 0), 0);
        const enteredPaid = checkoutForm.paidAmount !== "" && checkoutForm.paidAmount !== undefined
          ? safeNum(checkoutForm.paidAmount)
          : Math.max(0, totalAmount - safeNum(folioData?.payments));
        if (Math.abs(sum - enteredPaid) > 0.05) {
          toast.error(`Total of split payments (Rs. ${sum.toFixed(2)}) must equal the amount paid now (Rs. ${enteredPaid.toFixed(2)})`);
          setIsCheckingOut(false);
          return;
        }
      }

      const res = await apiClient.post(`/rooms/bookings/${selectedRoom.active_booking_id}/check-out`, {
        paymentType: checkoutForm.paymentType || "Cash",
        multiPayments: checkoutForm.multiPayments,
        note: checkoutForm.note || "",
        subTotal,
        discount,
        roundOff,
        serviceCharge,
        taxAmount,
        totalAmount,
        paidAmount: checkoutForm.paidAmount === "" ? null : Number(String(checkoutForm.paidAmount).replace(/,/g, ""))
      });

      toast.success(`Checked out! Invoice: ${res.data.invoiceNo}`);
      try {
        handlePrintFolio(null, false, res.data);
      } catch (printErr) {
        console.warn("Print folio warning on checkout:", printErr);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      console.error("Check-out error:", err);
      toast.error(err.response?.data?.message || err.message || "Check-out failed");
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Stats calculation
  const totalRooms = rooms.length;
  const occupiedRooms = rooms.filter(r => r.active_booking_id).length;
  const availableRooms = totalRooms - occupiedRooms;
  const dirtyRooms = rooms.filter(r => r.housekeeping_status === "Dirty").length;

  return (
    <div className="p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Room Status & PMS</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage guest rooms, bookings, folios and compliance invoicing.</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        {[
          { label: "Total Rooms", val: totalRooms, color: "border-gray-200 dark:border-zinc-800" },
          { label: "Occupied Rooms", val: occupiedRooms, color: "border-red-400 text-red-500" },
          { label: "Available Rooms", val: availableRooms, color: "border-green-400 text-green-500" },
          { label: "Dirty Rooms", val: dirtyRooms, color: "border-yellow-400 text-yellow-500" }
        ].map((stat, i) => (
          <div key={i} className={`p-5 rounded-2xl border bg-white dark:bg-zinc-900 shadow-sm flex flex-col justify-between ${stat.color}`}>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{stat.label}</span>
            <span className="text-3xl font-extrabold mt-2">{stat.val}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="rounded-full h-12 w-12 border-t-2 border-b-2 border-restro-green"></div>
        </div>
      ) : (
        <div className="space-y-10">
          {roomTypes.map(type => {
            const filteredRooms = rooms.filter(r => r.room_type_id === type.id);
            if (filteredRooms.length === 0) return null;
            return (
              <div key={type.id} className="space-y-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold tracking-tight text-gray-800 dark:text-zinc-200">{type.name}</h2>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white dark:bg-zinc-800 border border-gray-100 dark:border-zinc-800 text-gray-500">
                    Rs. {type.rate} / Night
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {filteredRooms.map(room => {
                    const isOccupied = !!room.active_booking_id;
                    const isArrival = !!room.arrival_booking_id && !isOccupied;
                    const housekeeping = room.housekeeping_status;
                    return (
                      <div
                        key={room.id}
                        onClick={() => handleRoomClick(room)}
                        className={`group relative p-4 rounded-2xl border text-left flex flex-col justify-between min-h-[168px] shadow-sm bg-white dark:bg-zinc-900 cursor-pointer ${
                          isOccupied 
                            ? "border-red-100 dark:border-red-950/30 hover:border-red-300" 
                            : isArrival ? "border-amber-100 dark:border-amber-950/30 hover:border-amber-300" : "border-green-100 dark:border-green-950/30 hover:border-green-300"
                        }`}
                      >
                        <div className="flex justify-between items-start w-full">
                          <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">{room.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                            isOccupied ? "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400" : 
                            isArrival ? "bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400" : 
                            "bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400"
                          }`}>
                            {isOccupied ? "Occupied" : isArrival ? "Arrival" : "Available"}
                          </span>
                        </div>

                        <div className="w-full">
                          {isOccupied ? (
                            <div className="mb-2">
                              <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 font-semibold">
                                <IconBedOff size={14} className="text-red-500 shrink-0" />
                                <span className="truncate">{room.occupant || "Guest"}</span>
                              </div>
                              {room.check_in && (
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap items-center gap-1 font-medium">
                                  <IconClock size={12} className="text-red-400 shrink-0" />
                                  <span>In: {moment(room.check_in).isValid() ? moment(room.check_in).format("DD MMM, hh:mm A") : room.check_in}</span>
                                  {getElapsedHoursText(room.check_in) && (
                                    <span className="text-rose-500 font-semibold whitespace-nowrap">({getElapsedHoursText(room.check_in)})</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : isArrival ? (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
                              <IconCalendar size={14} className="text-amber-500 shrink-0" />
                              <span className="truncate">{room.arrival_guest || "Reserved"}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
                              <IconBed size={14} className="text-green-500 shrink-0" />
                              <span>Ready</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center w-full pt-1">
                            <div className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${
                                housekeeping === "Clean" ? "bg-green-500" : housekeeping === "Dirty" ? "bg-yellow-500" : "bg-gray-400"
                              }`} />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{housekeeping}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {(isOccupied || isArrival) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRoomClick(room, "payment");
                                  }}
                                  className="p-1 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400 hover:bg-green-500 hover:text-white dark:hover:bg-green-500 dark:hover:text-white flex items-center justify-center transition"
                                  title="Collect Advance"
                                >
                                  <IconCash size={16} />
                                </button>
                              )}
                              {isOccupied && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRoomClick(room, "checkout");
                                  }}
                                  className="p-1 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 hover:bg-red-500 hover:text-white dark:hover:bg-red-500 dark:hover:text-white flex items-center justify-center transition"
                                  title="Check-out Room (चेक-आउट)"
                                >
                                  <IconCheck size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Booking Details / Folio Modal */}
      {isModalOpen && selectedRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-4xl shadow-sm flex flex-col max-h-[90vh] overflow-hidden">
            <ModalErrorBoundary>
              {/* Header */}
            <div className="p-6 border-b border-gray-100 dark:border-zinc-800 flex justify-between items-center bg-gray-50/50 dark:bg-zinc-900/30">
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Room {selectedRoom?.name || ""}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {selectedRoom?.room_type_name || ""}
                </p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Modal Tabs if Occupied */}
            {selectedRoom?.active_booking_id && (
              <div className="flex border-b border-gray-100 dark:border-zinc-800 px-6 bg-white dark:bg-zinc-950 overflow-x-auto">
                {[
                  { id: "folio", label: "Folio Details" },
                  { id: "charge", label: "Add Charges" },
                  { id: "payment", label: "Collect Advance" },
                  { id: "change_room", label: "Change Room" },
                  { id: "merge_room", label: "Merge Room" },
                  { id: "checkout", label: "Check-out" }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setModalTab(tab.id)}
                    className={`py-4 px-4 text-sm font-semibold border-b-2 -mb-px shrink-0 transition ${
                      modalTab === tab.id 
                        ? "border-restro-green text-restro-green" 
                        : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Modal Tabs if Reserved/Arrival (not yet occupied) */}
            {selectedRoom?.arrival_booking_id && !selectedRoom?.active_booking_id && (
              <div className="flex border-b border-gray-100 dark:border-zinc-800 px-6 bg-white dark:bg-zinc-950 overflow-x-auto">
                {[
                  { id: "folio", label: "Folio Details" },
                  { id: "payment", label: "Collect Advance" },
                  { id: "checkin", label: "Check-in Arrival" }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setModalTab(tab.id)}
                    className={`py-4 px-4 text-sm font-semibold border-b-2 -mb-px shrink-0 transition ${
                      modalTab === tab.id 
                        ? "border-restro-green text-restro-green" 
                        : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Modal Tabs if Available */}
            {!selectedRoom?.active_booking_id && !selectedRoom?.arrival_booking_id && (
              <div className="flex border-b border-gray-100 dark:border-zinc-800 px-6 bg-white dark:bg-zinc-950 overflow-x-auto">
                {[
                  { id: "checkin", label: "Instant Check-in" },
                  { id: "reserve", label: "Reserve Room" }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setModalTab(tab.id)}
                    className={`py-4 px-4 text-sm font-semibold border-b-2 -mb-px shrink-0 transition ${
                      modalTab === tab.id 
                        ? "border-restro-green text-restro-green" 
                        : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto flex-1 bg-white dark:bg-zinc-950">
              {(modalTab === "checkin" || modalTab === "reserve") && (
                <form onSubmit={handleCheckIn} className="space-y-6">
                  {!selectedRoom?.arrival_booking_id && (
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Select Existing Guest</label>
                      <select
                        onChange={handleCustomerSelect}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                      >
                        <option value="">-- Type new details below --</option>
                        {Array.isArray(customersList) && customersList.map(c => (
                          <option key={c.phone} value={c.phone}>{c.name} ({c.phone})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Guest Name</label>
                      <input
                        type="text"
                        required
                        value={checkInForm.guest_name}
                        onChange={e => setCheckInForm({ ...checkInForm, guest_name: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Phone Number</label>
                      <input
                        type="tel"
                        required
                        value={checkInForm.guest_phone}
                        onChange={e => setCheckInForm({ ...checkInForm, guest_phone: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        placeholder="98XXXXXXXX"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Email Address</label>
                      <input
                        type="email"
                        value={checkInForm.guest_email}
                        onChange={e => setCheckInForm({ ...checkInForm, guest_email: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        placeholder="guest@gmail.com"
                      />
                    </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Date & Time</label>
                        <input
                          type="datetime-local"
                          value={checkInForm.check_in}
                          onChange={e => setCheckInForm({ ...checkInForm, check_in: e.target.value })}
                          className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        />
                      </div>
                    </div>
                    {(modalTab === "checkin" || modalTab === "reserve") && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Advance Deposit</label>
                          <input
                            type="number"
                            value={checkInForm.advance_payment}
                            onChange={e => setCheckInForm({ ...checkInForm, advance_payment: e.target.value })}
                            className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                            min="0"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Payment Account</label>
                          <select
                            value={checkInForm.payment_method}
                            onChange={e => setCheckInForm({ ...checkInForm, payment_method: e.target.value })}
                            className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                          >
                            {Array.isArray(paymentTypes) && paymentTypes.filter(p => p.is_active).map(p => (
                              <option key={p.id} value={p.title}>{p.title}</option>
                            ))}
                            {(!Array.isArray(paymentTypes) || paymentTypes.filter(p => p.is_active).length === 0) && (
                              <>
                                <option value="Cash">Cash</option>
                                <option value="Bank">Bank Account</option>
                                <option value="Mobile Wallet">eSewa/Khalti</option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>
                    )}

                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Notes / Remarks</label>
                    <textarea
                      value={checkInForm.notes}
                      onChange={e => setCheckInForm({ ...checkInForm, notes: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none h-24 resize-none"
                      placeholder="Special guest requests, etc."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white flex items-center justify-center gap-2"
                  >
                    <IconUserPlus size={18} /> {modalTab === "reserve" ? "Confirm Reservation" : "Confirm Check-In"}
                  </button>
                </form>
              )}

              {/* TAB 2: FOLIO DETAILS VIEW */}
              {modalTab === "folio" && (
                <div className="space-y-6">
                  {folioLoading ? (
                    <div className="flex justify-center items-center py-10">
                      <div className="rounded-full h-8 w-8 border-t-2 border-b-2 border-restro-green"></div>
                    </div>
                  ) : folioData ? (
                    <div className="space-y-6">
                      {/* Guest Summary Card */}
                      <div className="p-5 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 flex flex-col md:flex-row justify-between gap-4">
                        <div>
                          <p className="text-xs text-gray-400 font-bold uppercase">Guest Information</p>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                            {folioData?.booking?.guest_name || "Guest"}
                          </h4>
                          <div className="text-xs text-gray-500 mt-1 space-y-1">
                            <p>Phone: {folioData?.booking?.guest_phone || "N/A"}</p>
                            <p>Email: {folioData?.booking?.guest_email || "N/A"}</p>
                          </div>
                        </div>
                        <div className="text-right flex flex-col justify-between items-end">
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Check-in Date</p>
                            <p className="text-sm font-semibold text-gray-700 dark:text-zinc-300 mt-1">
                              {folioData?.booking?.check_in ? new Date(folioData.booking.check_in).toLocaleString() : "N/A"}
                            </p>
                          </div>
                          <div className="mt-2 text-right">
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Nights & Rate</p>
                            <div className="mt-1 flex items-center justify-end gap-2">
                              <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-restro-green/10 text-restro-green">
                                {folioData?.nights || 1} Nights
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditStayForm({
                                    nights: folioData?.nights || 1,
                                    roomRate: folioData?.booking?.room_rate || 0
                                  });
                                  setIsEditingStay(!isEditingStay);
                                }}
                                className="px-2 py-0.5 rounded text-[11px] font-bold bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                              >
                                Edit Stay
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isEditingStay && (
                        <form onSubmit={handleSaveStayCustomization} className="p-5 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-150 dark:border-zinc-800 space-y-4">
                          <h5 className="text-sm font-bold text-gray-900 dark:text-white">Customize Stay & Pricing</h5>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-bold uppercase text-gray-400">Nights Stayed</label>
                              <input
                                type="number"
                                min="1"
                                className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                                value={editStayForm.nights}
                                onChange={e => setEditStayForm({ ...editStayForm, nights: e.target.value })}
                                required
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-[11px] font-bold uppercase text-gray-400">Custom Room Rate (Rs.)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                                value={editStayForm.roomRate}
                                onChange={e => setEditStayForm({ ...editStayForm, roomRate: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              type="button"
                              onClick={() => setIsEditingStay(false)}
                              className="px-4 py-2 text-xs font-bold rounded-xl border border-gray-250 dark:border-zinc-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="px-4 py-2 text-xs font-bold text-white rounded-xl bg-restro-green hover:bg-restro-green-button-hover"
                            >
                              Save Changes
                            </button>
                          </div>
                        </form>
                      )}

                      {/* Print Options Bar */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-gray-100 dark:border-zinc-800">
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Folio Print Options:</span>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handlePrintFolio(null, true)}
                            className="px-3 py-1.5 text-xs font-bold bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700 rounded-xl flex items-center gap-1.5 shadow-sm"
                            title="Print Estimate Bill / Proforma Folio (Not Final Bill / कच्चा बिल)"
                          >
                            <SafeIconPrinter size={15} /> Estimate Folio (Not Final)
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintFolio("pos_receipt")}
                            className="px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center gap-1.5 shadow-sm"
                            title="Print using Order Page POS Thermal Receipt Format (Pay and Complete format)"
                          >
                            <SafeIconPrinter size={15} /> POS Bill (Order Format)
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintFolio()}
                            className="px-3 py-1.5 text-xs font-bold bg-restro-green hover:bg-emerald-700 text-white rounded-xl flex items-center gap-1.5 shadow-sm"
                            title="Print using store default format selected in Settings"
                          >
                            <SafeIconPrinter size={15} /> Print Bill ({BILL_FORMATS.find(f => f.id === (printSettings?.billFormat || printSettings?.bill_format || 'pos_receipt'))?.shortName || 'Default'})
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintFolio("thermal_ird")}
                            className="px-2.5 py-1.5 text-xs font-medium bg-white hover:bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 border border-gray-200 dark:border-zinc-700 rounded-xl flex items-center gap-1"
                          >
                            Thermal (80mm)
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintFolio("standard_a4_a5")}
                            className="px-2.5 py-1.5 text-xs font-medium bg-white hover:bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 border border-gray-200 dark:border-zinc-700 rounded-xl flex items-center gap-1"
                          >
                            A4/A5 Sheet
                          </button>
                        </div>
                      </div>

                      {/* Folio Ledger table */}
                      <div className="border border-gray-100 dark:border-zinc-800 rounded-2xl overflow-hidden">
                        <table className="w-full text-left border-collapse text-sm">
                          <thead>
                            <tr className="bg-gray-50 dark:bg-zinc-900 border-b border-gray-100 dark:border-zinc-800">
                              <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Source</th>
                              <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Description</th>
                              <th className="p-4 text-right font-bold text-gray-500 uppercase tracking-wider text-[11px]">Amount (Rs.)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                            {/* Room Nights Charge */}
                            <tr>
                              <td className="p-4 font-bold">Room Charges</td>
                              <td className="p-4 text-gray-500">
                                Rs. {safeNum(folioData?.booking?.room_rate)} × {folioData?.nights || 1} Nights
                              </td>
                              <td className="p-4 text-right font-semibold">
                                {safeNum(folioData?.roomTotal).toFixed(2)}
                              </td>
                            </tr>
                            {/* Extra charges */}
                            {(folioData?.extraCharges || []).map(c => (
                              <tr key={c.id}>
                                <td className="p-4 font-bold text-amber-600 dark:text-amber-400">Extra Charge</td>
                                <td className="p-4 text-gray-500">{c.title}</td>
                                <td className="p-4 text-right font-semibold text-amber-600 dark:text-amber-400">
                                  {safeNum(c.amount).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                            {/* Restaurant Order charges */}
                            {(folioData?.orders || []).map(o => (
                              <tr key={o.id}>
                                <td className="p-4 font-bold text-blue-600 dark:text-blue-400">Restaurant Order</td>
                                <td className="p-4 text-gray-500">
                                  Order #{o.id} ({Array.isArray(o?.items) ? o.items.filter(item => item?.status !== 'cancelled').map(item => `${item?.item_name || 'Item'} ×${item?.quantity || 1}`).join(", ") : ""})
                                </td>
                                <td className="p-4 text-right font-semibold text-blue-600 dark:text-blue-400">
                                  {safeNum(o.order_total).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Folio Balance calculations */}
                      <div className="flex flex-col items-end gap-2.5">
                        <div className="flex justify-between w-64 text-sm">
                          <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Total Charges</span>
                          <span className="font-bold">Rs. {safeNum(folioData?.totalCharges).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center w-64 text-sm text-green-500 font-semibold">
                          <span className="font-bold uppercase tracking-wider text-[10px]">Advance Paid</span>
                          <div className="flex items-center gap-2">
                            <span>- Rs. {safeNum(folioData?.payments).toFixed(2)}</span>
                            <button
                              type="button"
                              onClick={() => setModalTab("payment")}
                              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold bg-green-500 hover:bg-green-600 text-white rounded-lg"
                              title="Collect Additional Advance"
                            >
                              <IconCash size={14} /> + Add Advance
                            </button>
                          </div>
                        </div>
                        <div className="border-t border-gray-100 dark:border-zinc-800 w-64 pt-2.5 flex justify-between w-64 text-base font-extrabold text-gray-900 dark:text-white">
                          <span>Balance Due</span>
                          <span>Rs. {safeNum(folioData?.balance).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setModalTab("checkout")}
                          className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 shadow-md transition"
                        >
                          <IconCheck size={18} /> Proceed to Check-out (बिल भुक्तानी तथा चेक-आउट)
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* TAB 3: ADD EXTRA CHARGES FORM */}
              {modalTab === "charge" && (
                <form onSubmit={handleAddCharge} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Service Description / Title</label>
                      <input
                        type="text"
                        required
                        value={extraChargeForm.title}
                        onChange={e => setExtraChargeForm({ ...extraChargeForm, title: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        placeholder="Laundry, Airport shuttle, Extra bed, etc."
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Amount (Rs.)</label>
                      <input
                        type="number"
                        required
                        value={extraChargeForm.amount}
                        onChange={e => setExtraChargeForm({ ...extraChargeForm, amount: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        min="1"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white flex items-center justify-center gap-2"
                  >
                    <IconPlus size={18} /> Add Service Charge to Folio
                  </button>
                </form>
              )}

              {/* TAB: RECORD PAYMENT */}
              {modalTab === "payment" && (
                <form onSubmit={handleRecordPayment} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Advance Payment Amount (Rs.)</label>
                      <input
                        type="number"
                        required
                        value={paymentForm.amount}
                        onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:ring-1 focus:ring-restro-green focus:outline-none"
                        min="1"
                        placeholder="1000"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Payment Account / Method</label>
                      <select
                        value={paymentForm.paymentMethod}
                        onChange={e => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                      >
                        {Array.isArray(paymentTypes) && paymentTypes.filter(p => p.is_active).map(p => (
                          <option key={p.id} value={p.title}>{p.title}</option>
                        ))}
                        {(!Array.isArray(paymentTypes) || paymentTypes.filter(p => p.is_active).length === 0) && (
                          <>
                            <option value="Cash">Cash</option>
                            <option value="Bank">Bank Account</option>
                            <option value="Mobile Wallet">eSewa/Khalti</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white flex items-center justify-center gap-2"
                  >
                    <IconCash size={18} /> Collect Advance Payment
                  </button>
                </form>
              )}

              {/* TAB: CHANGE ROOM */}
              {modalTab === "change_room" && (
                <form onSubmit={handleChangeRoom} className="space-y-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Move Guest to Room</label>
                    <select
                      value={targetRoomName}
                      onChange={e => setTargetRoomName(e.target.value)}
                      required
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                    >
                      <option value="">Select a vacant room</option>
                      {Array.isArray(rooms) && rooms
                        .filter(r => !r.active_booking_id)
                        .map(r => (
                          <option key={r.id} value={r.name}>
                            Room {r.name} ({r.room_type_name})
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white flex items-center justify-center gap-2"
                  >
                    <IconBed size={18} /> Confirm Room Change
                  </button>
                </form>
              )}

              {/* TAB: MERGE ROOM */}
              {modalTab === "merge_room" && (
                <form onSubmit={handleMergeRooms} className="space-y-6">
                  <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-amber-850 dark:text-amber-300 text-sm flex gap-3">
                    <IconInfoCircle size={20} className="shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Important Bill Merger Notice</p>
                      <p className="mt-1 text-xs leading-relaxed">
                        This action will merge the entire accrued bill of the current room (Room {selectedRoom?.name || ""}) 
                        including room night charges, extra services, and food orders, into the target room's bill. 
                        Room {selectedRoom?.name || ""} will be marked as Merged & Checked-out.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Merge Bill Into Room</label>
                    <select
                      value={targetBookingId}
                      onChange={e => setTargetBookingId(e.target.value)}
                      required
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                    >
                      <option value="">Select an occupied room</option>
                      {Array.isArray(rooms) && rooms
                        .filter(r => r.active_booking_id && r.id !== selectedRoom?.id)
                        .map(r => (
                          <option key={r.id} value={r.active_booking_id}>
                            Room {r.name} (Occupant: {r.occupant})
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-bold bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center gap-2"
                  >
                    <IconPlus size={18} /> Merge Bills & Close Room {selectedRoom?.name || ""}
                  </button>
                </form>
              )}

              {/* TAB 4: CHECKOUT FORM & INVOICE SUMMARY */}
              {modalTab === "checkout" && (
                folioLoading ? (
                  <div className="flex flex-col justify-center items-center py-16 gap-3">
                    <div className="rounded-full h-8 w-8 border-t-2 border-b-2 border-restro-green animate-spin"></div>
                    <p className="text-xs text-gray-400 font-medium">Loading guest folio details...</p>
                  </div>
                ) : folioData ? (
                <form onSubmit={handleCheckOut} className="space-y-6">
                  {/* Section: Additional Discount & Round Off */}
                  <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl shadow-sm p-4 space-y-4">
                    {/* Discount */}
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400 flex items-center gap-1.5">
                          <IconDiscount size={15} className="text-emerald-500" /> Apply Discount
                        </label>
                        <select 
                          value={checkoutForm.discountType}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, discountType: e.target.value, targetTotal: undefined })}
                          className="text-xs font-bold bg-gray-100 dark:bg-zinc-800 rounded-lg px-2 py-1 outline-none text-gray-700 dark:text-zinc-200"
                        >
                          <option value="fixed">Fixed (Rs.)</option>
                          <option value="percent">Percent (%)</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input 
                            type="number" 
                            min="0"
                            step="any"
                            value={checkoutForm.discount || ""} 
                            onChange={(e) => {
                              const discount = e.target.value === "" ? 0 : parseFloat(String(e.target.value).replace(/,/g, "")) || 0;
                              setCheckoutForm({ ...checkoutForm, discount, targetTotal: undefined });
                            }} 
                            placeholder="0.00"
                            className="w-full px-3 py-2 rounded-xl border border-gray-250 dark:border-zinc-800 bg-transparent text-sm font-semibold focus:ring-1 focus:ring-restro-green focus:outline-none"
                          />
                        </div>
                        {checkoutForm.discountType === "percent" && (
                          <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 rounded-xl whitespace-nowrap">
                            = Rs. {(((safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal)) * safeNum(checkoutForm.discount)) / 100).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Round Off */}
                    <div className="pt-2 border-t border-gray-100 dark:border-zinc-800">
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400 flex items-center gap-1.5">
                          <IconScale size={15} className="text-blue-500" /> Round Off
                        </label>
                        {Number(checkoutForm.roundOff || 0) !== 0 && (
                          <span className="text-[11px] font-bold text-blue-600">
                            {Number(checkoutForm.roundOff) > 0 ? `+Rs. ${Number(checkoutForm.roundOff).toFixed(2)}` : `-Rs. ${Math.abs(Number(checkoutForm.roundOff)).toFixed(2)}`}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {[
                          { label: "Floor 10", fn: (before) => Math.floor(before / 10) * 10 },
                          { label: "Nearest 10", fn: (before) => Math.round(before / 10) * 10 },
                          { label: "Floor 5", fn: (before) => Math.floor(before / 5) * 5 },
                          { label: "Nearest 1", fn: (before) => Math.round(before) },
                        ].map(preset => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                              const discountVal = safeNum(checkoutForm.discount);
                              const discountAmount = checkoutForm.discountType === "percent"
                                ? (subTotal * discountVal) / 100
                                : discountVal;
                              const afterDiscount = Math.max(0, subTotal - discountAmount);
                              const serviceCharge = safeNum(checkoutForm.serviceCharge);
                              const baseAmount = afterDiscount + serviceCharge;
                              let totalExclusiveTax = 0;
                              (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                                const rate = safeNum(tax.rate);
                                if (tax.type === "exclusive") {
                                  totalExclusiveTax += (baseAmount * rate) / 100;
                                }
                              });
                              const advancePaid = safeNum(folioData?.payments);
                              const before = Math.max(0, baseAmount + totalExclusiveTax - advancePaid);
                              const target = preset.fn(before);
                              const diff = parseFloat((target - before).toFixed(2));
                              setCheckoutForm(prev => ({ ...prev, roundOff: diff, targetTotal: undefined }));
                            }}
                            className="px-2.5 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 dark:bg-zinc-800 dark:hover:bg-zinc-700 rounded-lg text-[11px] font-bold transition text-gray-600 dark:text-zinc-300"
                          >
                            {preset.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setCheckoutForm(prev => ({ ...prev, roundOff: 0, targetTotal: undefined }))}
                          className="px-2.5 py-1 bg-gray-100 hover:bg-red-50 hover:text-red-600 dark:bg-zinc-800 rounded-lg text-[11px] font-bold transition text-gray-400"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1">Round Off (+/-)</label>
                          <input
                            type="number"
                            step="any"
                            value={checkoutForm.roundOff !== undefined && checkoutForm.roundOff !== 0 ? checkoutForm.roundOff : ""}
                            onChange={(e) => {
                              const val = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;
                              setCheckoutForm(prev => ({ ...prev, roundOff: val, targetTotal: undefined }));
                            }}
                            placeholder="0.00"
                            className="w-full px-3 py-2 rounded-xl border border-gray-250 dark:border-zinc-800 bg-transparent text-xs font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1">Target Rounded Total</label>
                          <input
                            type="number"
                            step="any"
                            value={
                              checkoutForm.targetTotal !== undefined
                                ? checkoutForm.targetTotal
                                : (() => {
                                    const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                                    const discountVal = safeNum(checkoutForm.discount);
                                    const discountAmount = checkoutForm.discountType === "percent"
                                      ? (subTotal * discountVal) / 100
                                      : discountVal;
                                    const afterDiscount = Math.max(0, subTotal - discountAmount);
                                    const serviceCharge = safeNum(checkoutForm.serviceCharge);
                                    const baseAmount = afterDiscount + serviceCharge;
                                    let totalExclusiveTax = 0;
                                    (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                                      const rate = safeNum(tax.rate);
                                      if (tax.type === "exclusive") {
                                        totalExclusiveTax += (baseAmount * rate) / 100;
                                      }
                                    });
                                    const advancePaid = safeNum(folioData?.payments);
                                    const before = Math.max(0, baseAmount + totalExclusiveTax - advancePaid);
                                    return Number((before + Number(checkoutForm.roundOff || 0)).toFixed(2));
                                  })()
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                setCheckoutForm(prev => ({ ...prev, targetTotal: "", roundOff: 0 }));
                                return;
                              }
                              const target = parseFloat(val);
                              const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                              const discountVal = safeNum(checkoutForm.discount);
                              const discountAmount = checkoutForm.discountType === "percent"
                                ? (subTotal * discountVal) / 100
                                : discountVal;
                              const afterDiscount = Math.max(0, subTotal - discountAmount);
                              const serviceCharge = safeNum(checkoutForm.serviceCharge);
                              const baseAmount = afterDiscount + serviceCharge;
                              let totalExclusiveTax = 0;
                              (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                                const rate = safeNum(tax.rate);
                                if (tax.type === "exclusive") {
                                  totalExclusiveTax += (baseAmount * rate) / 100;
                                }
                              });
                              const advancePaid = safeNum(folioData?.payments);
                              const before = Math.max(0, baseAmount + totalExclusiveTax - advancePaid);
                              if (!isNaN(target)) {
                                const diff = parseFloat((target - before).toFixed(2));
                                setCheckoutForm(prev => ({ ...prev, targetTotal: val, roundOff: diff }));
                              } else {
                                setCheckoutForm(prev => ({ ...prev, targetTotal: val }));
                              }
                            }}
                            onBlur={() => {
                              setCheckoutForm(prev => ({ ...prev, targetTotal: undefined }));
                            }}
                            placeholder="Target Bill"
                            className="w-full px-3 py-2 rounded-xl border border-gray-250 dark:border-zinc-800 bg-transparent text-xs font-bold text-restro-green focus:ring-1 focus:ring-restro-green focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary of checkout calculation */}
                  <div className="p-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 space-y-4">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Checkout Billing Summary</h4>
                    <div className="space-y-2.5 divide-y divide-zinc-200/50 dark:divide-zinc-800/50 text-sm">
                      <div className="flex justify-between pb-2">
                        <span className="text-gray-500">Subtotal (Rooms + Services + Orders)</span>
                        <span className="font-semibold">Rs. {(safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal)).toFixed(2)}</span>
                      </div>
                      {(() => {
                        const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                        const discountVal = safeNum(checkoutForm.discount);
                        const discountAmount = checkoutForm.discountType === "percent"
                          ? (subTotal * discountVal) / 100
                          : discountVal;
                        if (discountAmount <= 0) return null;
                        return (
                          <div className="flex justify-between pt-2 pb-2 text-emerald-600 font-medium">
                            <span>Discount {checkoutForm.discountType === "percent" ? `(${checkoutForm.discount}%)` : "(Fixed)"}</span>
                            <span>- Rs. {discountAmount.toFixed(2)}</span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between pt-2 pb-2">
                        <span className="text-gray-500">Service Charge</span>
                        <input
                          type="number"
                          className="w-28 p-1.5 text-right rounded-lg border border-gray-200 dark:border-zinc-800 bg-transparent focus:outline-none focus:ring-1 focus:ring-restro-green"
                          value={checkoutForm.serviceCharge}
                          onChange={e => setCheckoutForm({ ...checkoutForm, serviceCharge: e.target.value })}
                        />
                      </div>
                      {(() => {
                        const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                        const discountVal = safeNum(checkoutForm.discount);
                        const discountAmount = checkoutForm.discountType === "percent"
                          ? (subTotal * discountVal) / 100
                          : discountVal;
                        const baseAmount = Math.max(0, subTotal - discountAmount) + safeNum(checkoutForm.serviceCharge);
                        const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
                        
                        return (
                          <>
                            {(isVat || (Array.isArray(taxes) && taxes.length > 0)) && (
                              <div className="flex justify-between pt-2 pb-2">
                                <span className="text-gray-500">Taxable Amount</span>
                                <span className="font-semibold">Rs. {baseAmount.toFixed(2)}</span>
                              </div>
                            )}
                            
                            {(Array.isArray(taxes) ? taxes : []).map(tax => {
                              const rate = safeNum(tax.rate);
                              let taxAmt = 0;
                              if (tax.type === "exclusive") {
                                taxAmt = (baseAmount * rate) / 100;
                              } else if (tax.type === "inclusive") {
                                taxAmt = baseAmount - (baseAmount * (100 / (100 + rate)));
                              }
                              if (taxAmt <= 0 && rate === 0) return null;
                              
                              const taxTitle = isVat && rate === 13 ? '13% VAT' : `${tax.title} (${rate}% ${tax.type})`;
                              
                              return (
                                <div key={tax.id} className="flex justify-between pt-2 pb-2">
                                  <span className="text-gray-500">{taxTitle}</span>
                                  <span className="font-semibold">Rs. {taxAmt.toFixed(2)}</span>
                                </div>
                              );
                            })}
                            
                            {(!Array.isArray(taxes) || taxes.length === 0) && (
                              <div className="flex justify-between pt-2 pb-2">
                                <span className="text-gray-500">{isVat ? '13% VAT' : 'Tax (0%)'}</span>
                                <span className="font-semibold">Rs. 0.00</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="flex justify-between pt-2 pb-2 font-bold text-green-500">
                        <span>Advance Already Settled</span>
                        <span>- Rs. {safeNum(folioData?.payments).toFixed(2)}</span>
                      </div>
                      {Number(checkoutForm.roundOff || 0) !== 0 && (
                        <div className="flex justify-between pt-2 pb-2 text-blue-600 font-medium">
                          <span>Round Off</span>
                          <span>{Number(checkoutForm.roundOff) > 0 ? `+ Rs. ${Number(checkoutForm.roundOff).toFixed(2)}` : `- Rs. ${Math.abs(Number(checkoutForm.roundOff)).toFixed(2)}`}</span>
                        </div>
                      )}
                      {(() => {
                        const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                        const discountVal = safeNum(checkoutForm.discount);
                        const discountAmount = checkoutForm.discountType === "percent"
                          ? (subTotal * discountVal) / 100
                          : discountVal;
                        const baseAmount = Math.max(0, subTotal - discountAmount) + safeNum(checkoutForm.serviceCharge);
                        let totalExclusiveTax = 0;
                        (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                          const rate = safeNum(tax.rate);
                          if (tax.type === "exclusive") {
                            totalExclusiveTax += (baseAmount * rate) / 100;
                          }
                        });
                        const netPayable = Math.max(0, baseAmount + totalExclusiveTax - safeNum(folioData?.payments) + safeNum(checkoutForm.roundOff));
                        return (
                          <div className="flex justify-between pt-3 text-lg font-extrabold text-gray-900 dark:text-white">
                            <span>Net Payable at Checkout</span>
                            <span>Rs. {netPayable.toFixed(2)}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Settlement Method</label>
                        {(!checkoutForm.multiPayments || checkoutForm.multiPayments.length === 0) ? (
                          <button
                            type="button"
                            onClick={addCheckoutMultiPaymentRow}
                            className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                          >
                            + Split Payment
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setCheckoutForm(prev => ({ ...prev, multiPayments: [] }))}
                            className="text-xs font-bold text-red-500 hover:underline cursor-pointer"
                          >
                            Single Method
                          </button>
                        )}
                      </div>

                      {Array.isArray(checkoutForm.multiPayments) && checkoutForm.multiPayments.length > 0 ? (
                        <div className="space-y-2">
                          {checkoutForm.multiPayments.map((payment, index) => (
                            <div key={index} className="flex gap-2 items-center bg-gray-50 dark:bg-zinc-950 p-2.5 rounded-xl border border-gray-150 dark:border-zinc-800">
                              <select
                                value={payment.payment_type_id}
                                onChange={(e) => handleCheckoutMultiPaymentChange(index, 'payment_type_id', e.target.value)}
                                className="flex-1 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-1 ring-indigo-500"
                              >
                                {Array.isArray(paymentTypes) && paymentTypes.filter(p => p.is_active).map(pt => (
                                  <option key={pt.id} value={pt.id}>{pt.title}</option>
                                ))}
                                {(!Array.isArray(paymentTypes) || paymentTypes.filter(p => p.is_active).length === 0) && (
                                  <>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Credit Card</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="Mobile Wallet">eSewa/Khalti</option>
                                  </>
                                )}
                              </select>
                              <div className="relative w-32">
                                <input
                                  type="number"
                                  value={payment.amount}
                                  onChange={(e) => handleCheckoutMultiPaymentChange(index, 'amount', parseFloat(String(e.target.value).replace(/,/g, "")) || 0)}
                                  className="w-full bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg pl-8 pr-2 py-1.5 text-xs font-bold outline-none focus:ring-1 ring-indigo-500"
                                />
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">Rs.</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeCheckoutMultiPaymentRow(index)}
                                className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 rounded-full hover:bg-red-50 dark:hover:bg-red-950 cursor-pointer"
                              >
                                <IconTrash size={14} />
                              </button>
                            </div>
                          ))}
                          <div className="flex justify-between items-center pt-1">
                            <button
                              type="button"
                              onClick={addCheckoutMultiPaymentRow}
                              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                            >
                              + Add Payment Method
                            </button>
                            <span className="text-[11px] font-semibold text-gray-500">
                              Split Total: Rs. {(checkoutForm.multiPayments.reduce((acc, p) => acc + (parseFloat(String(p.amount || 0).replace(/,/g, "")) || 0), 0)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <select
                          value={checkoutForm.paymentType}
                          onChange={e => setCheckoutForm({ ...checkoutForm, paymentType: e.target.value })}
                          className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                        >
                          {Array.isArray(paymentTypes) && paymentTypes.filter(p => p.is_active).map(p => (
                            <option key={p.id} value={p.id}>{p.title}</option>
                          ))}
                          {(!Array.isArray(paymentTypes) || paymentTypes.filter(p => p.is_active).length === 0) && (
                            <>
                              <option value="Cash">Cash</option>
                              <option value="Card">Credit Card</option>
                              <option value="Bank Transfer">Bank Transfer</option>
                              <option value="Mobile Wallet">eSewa/Khalti</option>
                            </>
                          )}
                        </select>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Amount Paid Now</label>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <button
                            type="button"
                            onClick={() => {
                              const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                              const discountVal = safeNum(checkoutForm.discount);
                              const discountAmount = checkoutForm.discountType === "percent" ? (subTotal * discountVal) / 100 : discountVal;
                              const baseAmount = Math.max(0, subTotal - discountAmount) + safeNum(checkoutForm.serviceCharge);
                              let totalExclusiveTax = 0;
                              (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                                const rate = safeNum(tax.rate);
                                if (tax.type === "exclusive") totalExclusiveTax += (baseAmount * rate) / 100;
                              });
                              const netPayable = Math.max(0, baseAmount + totalExclusiveTax - safeNum(folioData?.payments) + safeNum(checkoutForm.roundOff));
                              setCheckoutForm(prev => ({ ...prev, paidAmount: netPayable.toFixed(2) }));
                            }}
                            className="px-2 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold dark:bg-emerald-950 dark:text-emerald-300 cursor-pointer"
                          >
                            Full Pay
                          </button>
                          <button
                            type="button"
                            onClick={() => setCheckoutForm(prev => ({ ...prev, paidAmount: "0" }))}
                            className="px-2 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold dark:bg-amber-950 dark:text-amber-300 cursor-pointer"
                          >
                            100% Due
                          </button>
                        </div>
                      </div>
                      <input
                        type="number"
                        placeholder="Leave empty for full payment"
                        value={checkoutForm.paidAmount}
                        onChange={e => setCheckoutForm({ ...checkoutForm, paidAmount: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                      />
                      {(() => {
                        const subTotal = safeNum(folioData?.roomTotal) + safeNum(folioData?.extraTotal) + safeNum(folioData?.orderTotal);
                        const discountVal = safeNum(checkoutForm.discount);
                        const discountAmount = checkoutForm.discountType === "percent" ? (subTotal * discountVal) / 100 : discountVal;
                        const baseAmount = Math.max(0, subTotal - discountAmount) + safeNum(checkoutForm.serviceCharge);
                        let totalExclusiveTax = 0;
                        (Array.isArray(taxes) ? taxes : []).forEach(tax => {
                          const rate = safeNum(tax.rate);
                          if (tax.type === "exclusive") totalExclusiveTax += (baseAmount * rate) / 100;
                        });
                        const netPayable = Math.max(0, baseAmount + totalExclusiveTax - safeNum(folioData?.payments) + safeNum(checkoutForm.roundOff));
                        if (checkoutForm.paidAmount === "") return null;
                        const enteredVal = parseFloat(checkoutForm.paidAmount) || 0;
                        const dueBal = Math.max(0, netPayable - enteredVal);
                        if (dueBal > 0) {
                          return (
                            <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center justify-between">
                              <span>Remaining Due (Accounts Receivable):</span>
                              <span className="font-extrabold text-sm">Rs. {dueBal.toFixed(2)}</span>
                            </div>
                          );
                        }
                        return (
                          <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-semibold text-emerald-700 dark:text-emerald-300 flex items-center justify-between">
                            <span>Settlement:</span>
                            <span className="font-bold">Full Payment (No Due)</span>
                          </div>
                        );
                      })()}
                    </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Checkout Note</label>
                      <input
                        type="text"
                        value={checkoutForm.note}
                        onChange={e => setCheckoutForm({ ...checkoutForm, note: e.target.value })}
                        className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                        placeholder="Optional remarks"
                      />
                    </div>


                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      onClick={async () => {
                         const reason = window.prompt("Please enter a reason for cancellation (this will appear in reports):");
                         if (reason !== null && reason.trim() !== "") {
                             try {
                                 toast.loading("Cancelling booking...");
                                 await apiClient.post(`/rooms/bookings/${selectedRoom?.active_booking_id}/cancel`, { cancelReason: reason.trim() });
                                 toast.dismiss();
                                 toast.success("Booking cancelled successfully!");
                                 setIsModalOpen(false);
                                 setSelectedRoom(null);
                                 fetchData();
                             } catch (e) {
                                 toast.dismiss();
                                 toast.error(e?.response?.data?.message || "Failed to cancel booking");
                             }
                         } else if (reason !== null) {
                             toast.error("Cancellation reason is required.");
                         }
                      }}
                      className="w-full sm:w-1/3 py-3.5 rounded-xl font-bold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 flex items-center justify-center gap-2 shadow-sm"
                      title="Cancel Booking (Mark as cancelled without payment)"
                    >
                      <IconX size={18} /> Cancel Booking
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePrintFolio(null, true)}
                      className="w-full sm:w-1/3 py-3.5 rounded-xl font-bold bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700 flex items-center justify-center gap-2 shadow-sm"
                      title="Print Estimate Bill (कच्चा बिल)"
                    >
                      <SafeIconPrinter size={18} /> Estimate
                    </button>
                    <button
                      type="submit"
                      disabled={isCheckingOut}
                      className="w-full sm:w-1/3 py-3.5 rounded-xl font-bold bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white flex items-center justify-center gap-2 shadow-md transition"
                    >

                      {isCheckingOut ? (
                        <>
                          <div className="rounded-full h-4 w-4 border-2 border-white border-t-transparent animate-spin"></div>
                          Processing Check-out...
                        </>
                      ) : (
                        <>
                          <IconCheck size={18} /> Confirm Check-out & Final Bill
                        </>
                      )}
                    </button>
                  </div>
                </form>
                ) : (
                  <div className="p-8 text-center space-y-4">
                    <p className="text-gray-500 font-medium text-sm">No active folio found or failed to load folio for this room.</p>
                    <button
                      type="button"
                      onClick={() => fetchFolio(selectedRoom?.active_booking_id)}
                      className="px-4 py-2 bg-restro-green text-white text-xs font-bold rounded-xl hover:bg-restro-green-button-hover"
                    >
                      Retry Loading Folio
                    </button>
                  </div>
                )
              )}
            </div>
            </ModalErrorBoundary>
          </div>
        </div>
      )}
    </div>
  );
}
